import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Coins, MousePointerClick, Percent, Ticket } from 'lucide-react'
import { WidgetFrame, WidgetState } from '@/components/growth/WidgetFrame'
import { granularityFor } from '@/components/growth/dashboardFilters'
import { describeChange, formatCompactShort, formatPercent } from '@/components/growth/formatters'
import { BarList } from '@/components/growth/widgets/shared/BarList'
import { FunnelChain } from '@/components/growth/widgets/shared/FunnelChain'
import { legendFor } from '@/components/growth/widgets/shared/legend'
import { MetricTile } from '@/components/growth/widgets/shared/MetricTile'
import { Segmented } from '@/components/growth/widgets/shared/StatGrid'
import { ChartLegend, TrendChart } from '@/components/growth/widgets/shared/TrendChart'
import widgetStyles from '@/components/growth/widgets/shared/Widgets.module.css'
import { affiliateLinksApi } from '@/services/api'
import {
  conversion,
  channelLabel,
  deviceLabel,
  formatCount,
  formatHandle,
  formatMoney,
  placementSourceLabel,
  stakeIn,
} from './affiliateLinksFormat'
import styles from './LiveLinksPage.module.css'

const TOP_LIMIT = 5

// What each number counts, behind the ⓘ on its card.
const SOURCES = {
  opens: 'Clicks on a link in the period. A link opens straight on the sportsbook, so every click is an open. The sharer opening their own link is not counted.',
  bets: 'Bets placed through a link in the period, by when they were placed. Self-referred bets (a player betting on their own link) and bets the sportsbook rejected are not counted.',
  conversion: 'Bets divided by opens in the period.',
  staked: 'The stake of the counted bets. Money is shown one currency at a time and never added across currencies.',
  ggr: 'Stake minus payout for settled credited bets. Void bets return the stake, so their GGR is zero. Excluding free bets counts a free bet\'s stake as zero.',
  settled: 'Credited bets with a settlement status.',
  funnel: 'Opens, the opens that added the bet to the betslip, and the bets placed. A bet counts every coupon placed from a link, so bets can outnumber adds.',
  trend: 'The same counts by UTC day, or by UTC hour for a range of two days or less.',
  affiliates: 'Affiliates ranked by the bets their marketing links brought in. Active affiliates are listed even with no activity.',
  sharers: 'Players ranked by the bets their shared links brought in.',
  placement: 'Bets by where the link was made: the player’s betslip, receipt or bet history, or Studio for marketing links.',
  sends: 'Taps on share options in the period, by channel.',
  via: 'Opens by the channel marker on the link. Booking-code opens are grouped separately.',
  device: 'Opens by the device family detected for the click.',
  sport: 'Links grouped by the sport saved on the share.',
}

function Card({ title, source, children }) {
  return (
    <WidgetFrame title={title} source={source}>
      <div className={widgetStyles.root}>{children}</div>
    </WidgetFrame>
  )
}

const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`

function changeFn(compare, previousTotals) {
  return (current, previous, options) => (
    compare && previousTotals && Number.isFinite(current) && Number.isFinite(previous)
      ? describeChange(current, previous, options)
      : null
  )
}

/** Opens, bets, conversion and stake for the period, each against the previous one. */
export function KeyMetrics({ summary, previous, compare, currency, currencies }) {
  const totals = summary.data?.totals
  const prior = previous?.data?.totals
  const changeFor = changeFn(compare, prior)
  const state = { loading: summary.isLoading, error: summary.error, onRetry: () => summary.refetch() }
  const rate = conversion(totals?.bets, totals?.opens)
  const priorRate = conversion(prior?.bets, prior?.opens)
  const staked = currency ? stakeIn(totals?.stake_by_currency, currency) : null
  const ggr = currency ? stakeIn(totals?.ggr_by_currency, currency) : null
  // Shown only when free bets make it differ from GGR.
  const cashGGR = currency && totals?.cash_ggr_by_currency?.[currency] !== undefined ? stakeIn(totals.cash_ggr_by_currency, currency) : null
  const otherCurrencies = Math.max(0, currencies.length - 1)
  const otherCurrenciesNote = otherCurrencies ? `Plus ${plural(otherCurrencies, 'other currency', 'other currencies')}` : null

  return (
    <div className={styles.kpis}>
      <Card title="Opens" source={SOURCES.opens}>
        <WidgetState {...state}>
          <MetricTile
            icon={MousePointerClick}
            value={formatCount(totals?.opens)}
            change={changeFor(totals?.opens, prior?.opens)}
            note={totals ? `${plural(totals.people, 'person', 'people')} reached` : null}
          />
        </WidgetState>
      </Card>
      <Card title="Bets" source={SOURCES.bets}>
        <WidgetState {...state}>
          <MetricTile
            icon={Ticket}
            value={formatCount(totals?.bets)}
            change={changeFor(totals?.bets, prior?.bets)}
            note={totals?.self_referral_bets ? `${plural(totals.self_referral_bets, 'self-referred bet', 'self-referred bets')} not counted` : null}
          />
        </WidgetState>
      </Card>
      <Card title="Conversion" source={SOURCES.conversion}>
        <WidgetState {...state}>
          <MetricTile
            icon={Percent}
            value={rate === null ? '—' : formatPercent(rate)}
            change={rate === null || priorRate === null ? null : changeFor(rate, priorRate, { points: true })}
            note={rate === null ? 'No opens yet' : null}
          />
        </WidgetState>
      </Card>
      <Card title="Staked" source={SOURCES.staked}>
        <WidgetState {...state}>
          <MetricTile
            icon={Coins}
            value={currency ? <span title={formatMoney(currency, staked)}>{formatMoney(currency, staked, { compact: true })}</span> : '—'}
            change={currency ? changeFor(staked, stakeIn(prior?.stake_by_currency, currency)) : null}
            note={currency
              ? otherCurrenciesNote
              : 'Nothing staked yet'}
          />
        </WidgetState>
      </Card>
      <Card title="GGR" source={SOURCES.ggr}>
        <WidgetState {...state}>
          <MetricTile
            icon={Coins}
            value={currency ? <span title={formatMoney(currency, ggr)}>{formatMoney(currency, ggr, { compact: true })}</span> : '—'}
            change={currency ? changeFor(ggr, stakeIn(prior?.ggr_by_currency, currency)) : null}
            note={currency
              ? (cashGGR !== null && cashGGR !== ggr ? `Excl. free bets ${formatMoney(currency, cashGGR, { compact: true })}` : otherCurrenciesNote)
              : 'Nothing settled yet'}
          />
        </WidgetState>
      </Card>
      <Card title="Settled bets" source={SOURCES.settled}>
        <WidgetState {...state}>
          <MetricTile
            icon={CheckCircle2}
            value={formatCount(totals?.settled_bets)}
            change={changeFor(totals?.settled_bets, prior?.settled_bets)}
            note={totals?.bets ? `${formatCount(totals.settled_bets || 0)} of ${plural(totals.bets, 'bet', 'bets')}` : null}
          />
        </WidgetState>
      </Card>
    </div>
  )
}

/** Opens → add rate → added to slip → slip-to-bet → bets, drawn as Growth's conversion funnel. */
export function ConversionWidget({ summary, previous, compare }) {
  const totals = summary.data?.totals
  const prior = previous?.data?.totals
  const changeFor = changeFn(compare, prior)
  const opens = totals?.opens ?? 0
  const adds = totals?.adds ?? 0
  const bets = totals?.bets ?? 0
  const rate = (value) => (value === null ? '—' : formatPercent(value))
  const addRate = opens ? adds / opens : null
  const betRate = adds ? bets / adds : null

  return (
    <Card title="Conversion funnel" source={SOURCES.funnel}>
      <WidgetState loading={summary.isLoading} error={summary.error} onRetry={() => summary.refetch()}>
        <div className={widgetStyles.conversion}>
          <FunnelChain
            label="Link conversion"
            steps={[
              { key: 'opens', kind: 'total', label: 'Opens', value: formatCount(opens), short: formatCompactShort(opens), count: opens, change: changeFor(opens, prior?.opens) },
              { key: 'add_rate', kind: 'rate', label: 'Add rate', value: rate(addRate) },
              { key: 'adds', kind: 'total', label: 'Added to slip', shortLabel: 'Added', value: formatCount(adds), short: formatCompactShort(adds), count: adds, change: changeFor(adds, prior?.adds) },
              { key: 'bet_rate', kind: 'rate', label: 'Slip to bet', shortLabel: 'To bet', value: rate(betRate) },
              { key: 'bets', kind: 'total', label: 'Bets', value: formatCount(bets), short: formatCompactShort(bets), count: bets, change: changeFor(bets, prior?.bets) },
            ]}
          />
        </div>
      </WidgetState>
    </Card>
  )
}

const TREND_METRICS = {
  opens: { label: 'Opens', read: (row) => row.opens, format: (value) => formatCount(value) },
  bets: { label: 'Bets', read: (row) => row.bets, format: (value) => formatCount(value) },
  staked: { label: 'Staked', read: (row, currency) => stakeIn(row.stake_by_currency, currency), money: true },
  // A bucket with no opens has no rate; it is drawn at zero, as Growth's
  // daily copy rate is.
  conversion: { label: 'Conversion', read: (row) => conversion(row.bets, row.opens) ?? 0, format: (value) => formatPercent(value) },
}
const TREND_CHOICES = Object.entries(TREND_METRICS).map(([value, metric]) => ({ value, label: metric.label }))

function seriesQuery(slug, params, granularity) {
  return {
    queryKey: ['affiliate-links', slug, 'series', params, granularity],
    queryFn: ({ signal }) => affiliateLinksApi.getSeries(slug, { ...params, ...(granularity === 'hour' ? { granularity } : {}) }, { signal }),
    enabled: Boolean(slug && params),
  }
}

/** One measure over the period, with the previous period dashed behind it. */
export function TrendWidget({ slug, range, pageParams, previousParams, compare, currency }) {
  const [metricKey, setMetricKey] = useState('opens')
  // Staked needs a currency; with nothing staked there is none to draw.
  const key = metricKey === 'staked' && !currency ? 'opens' : metricKey
  const metric = TREND_METRICS[key]
  const granularity = granularityFor(range)
  const current = useQuery(seriesQuery(slug, pageParams, granularity))
  const previous = useQuery({ ...seriesQuery(slug, previousParams, granularity), enabled: Boolean(slug && previousParams) })
  // The dashed line is drawn only when the previous period actually loaded.
  const comparing = Boolean(compare && previousParams) && !previous.isError
  const toRows = (data) => (data?.rows || []).map((row) => ({ date: row.date, value: metric.read(row, currency) }))
  const rows = toRows(current.data)
  const format = metric.money ? (value) => formatMoney(currency, value, { compact: true }) : metric.format
  const choices = currency ? TREND_CHOICES : TREND_CHOICES.filter((choice) => choice.value !== 'staked')

  return (
    <Card title="Performance over time" source={SOURCES.trend}>
      <div className={widgetStyles.chart}>
        <div className={widgetStyles.chartToolbar}>
          <Segmented label="Trend metric" value={key} onChange={setMetricKey} items={choices} />
          <div className={styles.legendLine}>
            <ChartLegend items={legendFor(metric.label, comparing)} />
            {compare && previousParams && previous.isError ? (
              <span className={styles.legendError}>
                Previous period could not load
                <button type="button" className={styles.inlineButton} onClick={() => previous.refetch()}>Retry</button>
              </span>
            ) : null}
          </div>
        </div>
        <WidgetState
          loading={current.isLoading}
          error={current.error}
          onRetry={() => current.refetch()}
          empty={!current.isLoading && rows.length < 2}
          emptyTitle="Not enough data to draw a trend"
        >
          <TrendChart
            rows={rows}
            previous={comparing ? toRows(previous.data) : []}
            format={format}
            label={`${metric.label} by ${granularity}`}
          />
        </WidgetState>
      </div>
    </Card>
  )
}

function RankedList({ items, total, noun, onSelect, format = (value) => formatCount(value), label }) {
  return (
    <div className={styles.ranked}>
      <BarList items={items} format={format} label={label} onSelect={onSelect} />
      {total ? <p className={widgetStyles.caveat}>{`${formatCount(items.length)} of ${plural(total, noun, `${noun}s`)}`}</p> : null}
    </div>
  )
}

/** The affiliates whose marketing links brought in the most bets. */
export function TopAffiliatesWidget({ slug, params: baseParams, onSelect }) {
  const params = baseParams ? { ...baseParams, sort_by: 'bets', sort_dir: 'desc', limit: TOP_LIMIT, offset: 0 } : null
  const query = useQuery({
    queryKey: ['affiliate-links', slug, 'affiliate-stats', params],
    queryFn: ({ signal }) => affiliateLinksApi.listAffiliateStats(slug, params, { signal }),
    enabled: Boolean(slug && params),
  })
  const items = (query.data?.items || []).map((row) => ({
    key: row.affiliate.id,
    label: `${row.affiliate.name} ${formatHandle(row.affiliate.handle)}`,
    avatar: { name: row.affiliate.name },
    value: row.stats?.bets || 0,
  }))
  return (
    <Card title="Top affiliates by bets" source={SOURCES.affiliates}>
      <WidgetState loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} empty={!query.isLoading && !items.length} emptyTitle="No affiliates yet" emptyDescription="Add affiliates to make marketing links for them.">
        <RankedList items={items} total={query.data?.total} noun="affiliate" label="Top affiliates by bets" onSelect={(item) => onSelect(item.key)} />
      </WidgetState>
    </Card>
  )
}

/** The players whose shared links brought in the most bets. */
export function TopSharersWidget({ slug, params: baseParams, onSelect }) {
  const params = baseParams ? { ...baseParams, sort_by: 'bets', sort_dir: 'desc', limit: TOP_LIMIT, offset: 0 } : null
  const query = useQuery({
    queryKey: ['affiliate-links', slug, 'sharers', 'top', params],
    queryFn: ({ signal }) => affiliateLinksApi.listSharers(slug, params, { signal }),
    enabled: Boolean(slug && params),
  })
  const items = (query.data?.items || []).map((row) => ({
    key: row.feed_user_id,
    label: row.kambi_player_id || 'Player ID not captured',
    playerId: row.kambi_player_id,
    value: row.stats?.bets || 0,
  }))
  return (
    <Card title="Top sharers by bets" source={SOURCES.sharers}>
      <WidgetState loading={query.isLoading} error={query.error} onRetry={() => query.refetch()} empty={!query.isLoading && !items.length} emptyTitle="No player links in this period">
        <RankedList
          items={items}
          total={query.data?.total}
          noun="sharer"
          label="Top sharers by bets"
          onSelect={(item) => { if (item.playerId) onSelect(item.playerId) }}
        />
      </WidgetState>
    </Card>
  )
}

/** Bets by where each link was made. */
export function PlacementWidget({ summary }) {
  const rows = summary.data?.by_placement || []
  const items = rows.map((row) => ({ key: row.key, label: placementSourceLabel(row.key), value: row.bets || 0 }))
  return (
    <Card title="Bets by where links were made" source={SOURCES.placement}>
      <WidgetState loading={summary.isLoading} error={summary.error} onRetry={() => summary.refetch()} empty={!summary.isLoading && !items.length} emptyTitle="No links in this period">
        <RankedList items={items} label="Bets by where links were made" />
      </WidgetState>
    </Card>
  )
}

function SummaryBreakdownWidget({ summary, title, source, rows, readValue, labelFor, emptyTitle, listLabel, noun }) {
  // Largest first by the number shown, and only as many rows as the card fits.
  const all = (rows || [])
    .map((row) => ({ key: row.key, label: labelFor(row.key), value: readValue(row) }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
  const items = all.slice(0, TOP_LIMIT)
  return (
    <Card title={title} source={source}>
      <WidgetState loading={summary.isLoading} error={summary.error} onRetry={() => summary.refetch()} empty={!summary.isLoading && !items.length} emptyTitle={emptyTitle}>
        <RankedList items={items} total={all.length > items.length ? all.length : undefined} noun={noun} label={listLabel || title} />
      </WidgetState>
    </Card>
  )
}

export function SendsByChannelWidget({ summary }) {
  return (
    <SummaryBreakdownWidget
      summary={summary}
      title="Shares sent by channel"
      source={SOURCES.sends}
      rows={summary.data?.sends_by_channel}
      readValue={(row) => row.count || 0}
      labelFor={channelLabel}
      noun="channel"
      emptyTitle="No shares sent in this period"
    />
  )
}

export function OpensByChannelWidget({ summary }) {
  return (
    <SummaryBreakdownWidget
      summary={summary}
      title="Opens by channel"
      source={SOURCES.via}
      rows={summary.data?.by_via}
      readValue={(row) => row.opens || 0}
      labelFor={channelLabel}
      noun="channel"
      emptyTitle="No opens in this period"
    />
  )
}

export function OpensByDeviceWidget({ summary }) {
  return (
    <SummaryBreakdownWidget
      summary={summary}
      title="Opens by device"
      source={SOURCES.device}
      rows={summary.data?.by_device}
      readValue={(row) => row.opens || 0}
      labelFor={deviceLabel}
      noun="device"
      emptyTitle="No opens in this period"
    />
  )
}

export function LinksBySportWidget({ summary }) {
  return (
    <SummaryBreakdownWidget
      summary={summary}
      title="Links by sport"
      source={SOURCES.sport}
      rows={summary.data?.by_sport}
      readValue={(row) => row.links || 0}
      labelFor={(key) => key === 'unknown' ? 'Unknown' : key}
      noun="sport"
      emptyTitle="No links in this period"
    />
  )
}
